import type { SevenZipModule, SevenZipModuleFactory } from '7z-wasm'
import JSZip from 'jszip'
import { compact, endsWith, last, toLower } from 'lodash'

import { isArchiveFile, isHTMLDocument, isPDFDocument } from '@/utils'
import { EArchiveExtension } from '@/utils/constants'

/**
 * Error information for a file that failed to extract
 */
export type ExtractionError = {
  fileName: string
  error: string
  file?: File
}

/**
 * Result of archive extraction
 */
export type ExtractionResult = {
  files: File[]
  errors: ExtractionError[]
}

/**
 * Check if a file path is a macOS metadata file
 * macOS creates these files in ZIP archives:
 * - Files in __MACOSX directories
 * - Files starting with ._ (resource fork files)
 */
const isMacOSMetadataFile = (filePath: string): boolean => {
  const normalizedPath = filePath.replace(/\\/g, '/') //NOSONAR - normalize backslashes to slashes
  const pathParts = normalizedPath.split('/')

  return (
    pathParts.includes('__MACOSX') ||
    pathParts[pathParts.length - 1]?.startsWith('._')
  )
}

/**
 * Get MIME type based on file extension
 */
const getMimeType = (fileName: string): string => {
  const lowerName = toLower(fileName)
  if (isHTMLDocument(fileName)) return 'text/html'
  if (endsWith(lowerName, '.zip') || endsWith(lowerName, '.7z')) {
    return 'application/octet-stream'
  }
  return 'application/octet-stream'
}

const extractFileName = (path: string): string => {
  return last(path.split('/')) || path
}

const createFileFromData = (
  content: Blob | Uint8Array,
  fileName: string
): File => {
  const blob =
    content instanceof Blob
      ? content
      : new Blob([content as unknown as BlobPart])
  return new File([blob], fileName, {
    type: getMimeType(fileName),
    lastModified: Date.now(),
  })
}

/**
 * Handle extraction of a nested archive file
 */
const handleNestedArchive = async (
  extractedFile: File,
  fileName: string,
  processedArchives: Set<string>,
  extractNestedArchive: (
    file: File,
    processed: Set<string>
  ) => Promise<ExtractionResult>
): Promise<ExtractionResult> => {
  const newProcessed = new Set(processedArchives)
  newProcessed.add(fileName.toLowerCase())

  try {
    return await extractNestedArchive(extractedFile, newProcessed)
  } catch (error) {
    return {
      files: [],
      errors: [
        {
          file: extractedFile,
          fileName,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to extract nested archive',
        },
      ],
    }
  }
}

/**
 * Check if an archive should be skipped
 */
const shouldSkipArchive = (
  fileName: string,
  currentArchiveName: string,
  processedArchives: Set<string>
): boolean => {
  const archiveName = fileName.toLowerCase()
  return (
    archiveName === currentArchiveName || processedArchives.has(archiveName)
  )
}

/**
 * Process extracted files: filter HTML files and handle nested archives
 */
const processExtractedFiles = async (
  extractedFiles: File[],
  currentArchiveName: string,
  processedArchives: Set<string>,
  extractNestedArchive: (
    file: File,
    processed: Set<string>
  ) => Promise<ExtractionResult>
): Promise<ExtractionResult> => {
  const files: File[] = []
  const errors: ExtractionError[] = []

  for (const extractedFile of extractedFiles) {
    const fileName = extractedFile.name

    if (isArchiveFile(extractedFile.name)) {
      if (shouldSkipArchive(fileName, currentArchiveName, processedArchives)) {
        continue
      }

      const result = await handleNestedArchive(
        extractedFile,
        fileName,
        processedArchives,
        extractNestedArchive
      )
      files.push(...result.files)
      errors.push(...result.errors)
    } else if (isHTMLDocument(fileName) || isPDFDocument(fileName)) {
      files.push(extractedFile)
    }
  }

  return { files, errors }
}

/**
 * Get error message for ZIP extraction failure
 */
const getZipErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    if (
      error.message.includes('corrupt') ||
      error.message.includes('invalid')
    ) {
      return 'Corrupted ZIP file. Please check the archive and try again.'
    }
    if (error.message.includes('password')) {
      return 'Password-protected ZIP files are not supported.'
    }
    return error.message
  }
  return 'Unknown error occurred while extracting ZIP file'
}

/**
 * Extract a single file from ZIP entry
 */
const extractZipEntry = async (
  zipEntry: JSZip.JSZipObject,
  relativePath: string,
  errors: ExtractionError[]
): Promise<File | null> => {
  try {
    const content = await zipEntry.async('blob')
    const fileName = extractFileName(relativePath)
    return createFileFromData(content, fileName)
  } catch (error) {
    errors.push({
      fileName: relativePath,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to extract file from ZIP',
    })
    return null
  }
}

/**
 * Extract files from a ZIP archive
 */
const extractZip = async (
  file: File,
  extractNestedArchive: (
    file: File,
    processed: Set<string>
  ) => Promise<ExtractionResult>,
  processedArchives: Set<string> = new Set()
): Promise<ExtractionResult> => {
  const errors: ExtractionError[] = []
  const currentArchiveName = file.name.toLowerCase()

  try {
    const arrayBuffer = await file.arrayBuffer()
    const zip = await JSZip.loadAsync(arrayBuffer)

    // Extract all files from ZIP
    const filePromises = Object.keys(zip.files).map(async (relativePath) => {
      const zipEntry = zip.files[relativePath]

      // Skip directories and macOS metadata
      if (zipEntry.dir || isMacOSMetadataFile(relativePath)) {
        return null
      }

      return extractZipEntry(zipEntry, relativePath, errors)
    })

    const results = await Promise.all(filePromises)
    const extractedFiles = compact(results)

    // Process extracted files (handle nested archives and filter HTML)
    const result = await processExtractedFiles(
      extractedFiles,
      currentArchiveName,
      processedArchives,
      extractNestedArchive
    )

    return {
      files: result.files,
      errors: [...errors, ...result.errors],
    }
  } catch (error) {
    return {
      files: [],
      errors: [{ file, fileName: file.name, error: getZipErrorMessage(error) }],
    }
  }
}

// 7z-wasm related types and utilities
let sevenZipInstancePromise: Promise<SevenZipModule> | null = null

type SevenZipExtractedFile = {
  path: string
  size: number
  bytes: Uint8Array
}

type SevenZipExtractOptions = {
  inFileName?: string
  outDir?: string
  cleanDirs?: boolean
}

type ArchiveInput = File | Blob | ArrayBuffer | Uint8Array

/**
 * Lazy-load singleton 7z-wasm instance
 */
const getSevenZipInstance = (): Promise<SevenZipModule> => {
  sevenZipInstancePromise ??= (async () => {
    const mod = (await import('7z-wasm')) as unknown as {
      default: SevenZipModuleFactory
    }
    return mod.default()
  })()
  return sevenZipInstancePromise
}

const sanitizeName = (name: string): string => {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_') || 'archive.7z' // NOSONAR - replace invalid characters
}

const toUint8Array = async (input: ArchiveInput): Promise<Uint8Array> => {
  if (input instanceof Uint8Array) return input
  if (input instanceof ArrayBuffer) return new Uint8Array(input)
  return new Uint8Array(await input.arrayBuffer())
}

/**
 * Recursively remove all files and subdirectories in a directory
 */
const removeDirRecursive = (sevenZip: SevenZipModule, dir: string): void => {
  try {
    const entries = sevenZip.FS.readdir(dir).filter(
      (n) => n !== '.' && n !== '..'
    )

    for (const name of entries) {
      const full = dir === '/' ? `/${name}` : `${dir}/${name}`
      const stat = sevenZip.FS.stat(full)

      if (sevenZip.FS.isDir(stat.mode)) {
        removeDirRecursive(sevenZip, full)
        sevenZip.FS.rmdir(full)
      } else if (sevenZip.FS.isFile(stat.mode)) {
        sevenZip.FS.unlink(full)
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to remove directory recursively:', error)
  }
}

/**
 * Recursively walk a directory in 7z-wasm FS and collect files
 */
const walkDirFiles = (
  sevenZip: SevenZipModule,
  dir: string,
  baseDir: string,
  out: SevenZipExtractedFile[]
): void => {
  const entries = sevenZip.FS.readdir(dir).filter(
    (n) => n !== '.' && n !== '..'
  )

  for (const name of entries) {
    const full = dir === '/' ? `/${name}` : `${dir}/${name}`
    const stat = sevenZip.FS.stat(full)

    if (sevenZip.FS.isDir(stat.mode)) {
      walkDirFiles(sevenZip, full, baseDir, out)
    } else if (sevenZip.FS.isFile(stat.mode)) {
      const relPath = full.startsWith(baseDir)
        ? full.slice(baseDir.length).replace(/^\/+/, '')
        : full.replace(/^\/+/, '')

      out.push({
        path: relPath || name,
        size: sevenZip.FS.readFile(full).byteLength,
        bytes: sevenZip.FS.readFile(full),
      })
    }
  }
}

/**
 * Clean directories in 7z-wasm filesystem
 */
const cleanDirectories = (
  sevenZip: SevenZipModule,
  outDir: string,
  inDir: string
): void => {
  const cleanDir = (dir: string) => {
    try {
      removeDirRecursive(sevenZip, dir)
      sevenZip.FS.rmdir(dir)
      try {
        sevenZip.FS.mkdir(dir)
      } catch {
        // Directory already exists
      }
    } catch {
      // Ignore cleanup errors
    }
  }

  cleanDir(outDir)
  cleanDir(inDir)
}

/**
 * Write input file to 7z-wasm virtual filesystem
 */
const writeInputFile = async (
  sevenZip: SevenZipModule,
  input: ArchiveInput,
  inDir: string,
  options: SevenZipExtractOptions
): Promise<string> => {
  const inputBytes = await toUint8Array(input)
  const fileName =
    'name' in input && typeof input.name === 'string'
      ? sanitizeName(input.name)
      : sanitizeName(options.inFileName ?? 'archive.7z')

  const inPath = `${inDir}/${fileName}`
  sevenZip.FS.writeFile(inPath, inputBytes)
  return inPath
}

/**
 * Initialize and clean 7z-wasm directories
 */
const initializeDirectories = (
  sevenZip: SevenZipModule,
  inDir: string,
  outDir: string,
  cleanDirs: boolean
): void => {
  const ensureDir = (dir: string) => {
    try {
      sevenZip.FS.mkdir(dir)
    } catch {
      // Directory already exists
    }
  }

  ensureDir(inDir)
  ensureDir(outDir)

  if (cleanDirs) {
    cleanDirectories(sevenZip, outDir, inDir)
  }
}

/**
 * Extract an archive (7z/zip/rar/etc) entirely in memory using 7z-wasm
 */
const extractArchive7z = async (
  input: ArchiveInput,
  options: SevenZipExtractOptions = {}
): Promise<SevenZipExtractedFile[]> => {
  const sevenZip = await getSevenZipInstance()
  const inDir = '/in'
  const outDir = options.outDir ?? '/out'

  initializeDirectories(sevenZip, inDir, outDir, options.cleanDirs ?? true)

  const inPath = await writeInputFile(sevenZip, input, inDir, options)

  // Extract archive
  const code = sevenZip.callMain([
    'x',
    inPath,
    `-p${'__no_password__'}`,
    `-o${outDir}`,
    '-y',
  ])
  // code === 0 means success
  if ((code as unknown as number) !== 0) {
    throw new Error(
      'Failed to extract 7Z file. Please check the archive and try again.'
    )
  }

  // Collect extracted files
  const files: SevenZipExtractedFile[] = []
  walkDirFiles(sevenZip, outDir, outDir, files)

  // Clean up input file after extraction
  try {
    sevenZip.FS.unlink(inPath)
  } catch {
    // Ignore cleanup errors
  }

  return files
}

/**
 * Convert extracted 7z data to File objects
 */
const convertExtractedData = (
  extractedFilesData: SevenZipExtractedFile[],
  file: File,
  errors: ExtractionError[]
): File[] => {
  const extractedFiles: File[] = []
  for (const fileData of extractedFilesData) {
    if (isMacOSMetadataFile(fileData.path)) {
      continue
    }

    try {
      const fileName = extractFileName(fileData.path)
      const extractedFile = createFileFromData(fileData.bytes, fileName)
      extractedFiles.push(extractedFile)
    } catch (error) {
      errors.push({
        file: file,
        fileName: file.name,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to create file from extracted data',
      })
    }
  }
  return extractedFiles
}

/**
 * Get error message for 7z extraction failure
 */
const get7zErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    if (
      error.message.includes('corrupt') ||
      error.message.includes('invalid')
    ) {
      return 'Corrupted 7Z file. Please check the archive and try again.'
    }
    if (error.message.includes('password')) {
      return 'Password-protected 7Z files are not supported.'
    }
    return error.message
  }
  return 'Unknown error occurred while extracting 7Z file'
}

/**
 * Extract files from a 7Z archive
 */
const extract7z = async (
  file: File,
  extractNestedArchive: (
    file: File,
    processed: Set<string>
  ) => Promise<ExtractionResult>,
  processedArchives: Set<string> = new Set()
): Promise<ExtractionResult> => {
  const errors: ExtractionError[] = []
  const currentArchiveName = file.name.toLowerCase()

  try {
    const extractedFilesData = await extractArchive7z(file, {
      outDir: '/out',
      cleanDirs: true,
    })

    const extractedFiles = convertExtractedData(
      extractedFilesData,
      file,
      errors
    )

    const result = await processExtractedFiles(
      extractedFiles,
      currentArchiveName,
      processedArchives,
      extractNestedArchive
    )

    return {
      files: result.files,
      errors: [...errors, ...result.errors],
    }
  } catch (error) {
    return {
      files: [],
      errors: [{ file, fileName: file.name, error: get7zErrorMessage(error) }],
    }
  }
}

/**
 * Extract files from an archive (ZIP or 7Z)
 * @param file - The archive file to extract
 * @param processedArchives - Set of archive names already processed (to prevent infinite loops)
 * @returns Object containing extracted files and any errors encountered
 */
export const extractArchive = async (
  file: File,
  processedArchives: Set<string> = new Set()
): Promise<ExtractionResult> => {
  const fileName = file.name.toLowerCase()

  if (fileName.endsWith(EArchiveExtension.ZIP)) {
    return extractZip(file, extractArchive, processedArchives)
  }

  if (fileName.endsWith(EArchiveExtension.SEVEN_ZIP)) {
    return extract7z(file, extractArchive, processedArchives)
  }

  return {
    files: [],
    errors: [
      {
        file,
        fileName: file.name,
        error: 'Unsupported archive format. Please use a ZIP or 7Z file.',
      },
    ],
  }
}
